import { Injectable } from '@angular/core';
import { State, Action, Selector, StateContext } from '@ngxs/store';
import {
  AddressItemCreate,
  AddressItemDelete,
  AddressItemInitialState,
  AddressItemLoad,
  AddressItemUpdate,
} from './address-item.actions';
import { AddressDetailsModel } from '../models/address.model';
import { catchError, map, take } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { NotificationService } from '../../../../common/services/notification/notification.service';
import { AddressItemService } from './address-item.service';
import { NavigateUserService } from '../../../services/navigate-user.service';
import { Error, CustomHttpErrorResponse } from '../../../../common';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface AddressItemStateModel extends AddressDetailsModel {
  loaded: boolean;
}

const initialState: AddressItemStateModel = {
  id: null,
  name: null,
  street: null,
  houseNumber: null,
  cityName: null,
  postCode: null,
  country: null,
  longitude: 0,
  latitude: 0,
  source: null,
  fullAddress: null,
  plantGate: null,
  plantGateId: null,
  deviation: 0,
  loaded: false,
  extId: null,
  creationDate: null,
};

@State<AddressItemStateModel>({
  name: 'addressItem',
  defaults: initialState,
})
@Injectable()
export class AddressItemState {
  constructor(
    private addressItemService: AddressItemService,
    private navigateUserService: NavigateUserService,
    private notificationService: NotificationService,
  ) {}
  @Selector()
  static getState(state: AddressItemStateModel): AddressItemStateModel {
    return state;
  }
  @Selector()
  static getAddressId(state: AddressItemStateModel): number {
    return state.id;
  }
  @Selector()
  static getAddressName(state: AddressItemStateModel): string {
    return state.name;
  }
  @Selector()
  static getLoaded(state: AddressItemStateModel): boolean {
    return state.loaded;
  }

  @Action(AddressItemLoad)
  getAddressDetails(
    ctx: StateContext<AddressItemStateModel>,
    { payload }: AddressItemLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.addressItemService.getAddressDetails(payload).pipe(
      take(1),
      map((addressDetails: AddressDetailsModel) => {
        ctx.patchState({ ...addressDetails, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AddressItemCreate)
  createAddress(
    ctx: StateContext<AddressItemStateModel>,
    { payload }: AddressItemCreate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.addressItemService.createAddress(payload).pipe(
      take(1),
      map(() => {
        this.notificationService.success('api.address.success.create');
        this.navigateUserService.navigateUserToAbsolutePath('/dashboard/administration/address-management');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AddressItemUpdate)
  updateAddress(
    ctx: StateContext<AddressItemStateModel>,
    { payload }: AddressItemUpdate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.addressItemService.updateAddress(payload).pipe(
      take(1),
      map((addressDetails: AddressDetailsModel) => {
        this.notificationService.success('api.address.success.update');
        ctx.patchState({ ...addressDetails, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AddressItemDelete)
  deleteAddress(
    ctx: StateContext<AddressItemStateModel>,
    { payload }: AddressItemDelete,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | Observable<void>> {
    return this.addressItemService.deleteAddress(payload.addressId).pipe(
      take(1),
      map(() => {
        payload.dialog.close();
        this.notificationService.success('api.address.success.delete');
        this.navigateUserService.navigateUserToAbsolutePath('/dashboard/administration/address-management');
        return ctx.dispatch(new AddressItemInitialState());
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AddressItemInitialState)
  setInitialState(ctx: StateContext<AddressItemStateModel>): void {
    ctx.setState(initialState);
  }
}
